import { Pause, PlayArrow } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../Auth/AuthContext";
import { formatCurrency } from "../utils/currencyFormatter";
import { formatDateForEdit } from "../utils/dateUtils";

const RecurringPaymentsTable = ({
  title,
  fetchFunction,
  deleteFunction,
  pauseFunction,
  resumeFunction,
  editPath,
  addPath,
}) => {
  const [payments, setPayments] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [orderBy, setOrderBy] = useState("next_due_date");
  const [order, setOrder] = useState("asc");
  const [searchQuery, setSearchQuery] = useState("");
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { userCurrency } = useContext(AuthContext);

  useEffect(() => {
    fetchPayments();
  }, [fetchFunction, showActiveOnly]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const data = await fetchFunction();
      const formattedData = data.map((payment) => ({
        ...payment,
        start_date: payment.start_date
          ? formatDateForEdit(payment.start_date)
          : null,
        next_due_date: payment.next_due_date
          ? formatDateForEdit(payment.next_due_date)
          : null,
        end_date: payment.end_date ? formatDateForEdit(payment.end_date) : null,
        // Normalize status
        status: payment.status || "active",
      }));
      setPayments(formattedData);
    } catch (error) {
      console.error("Error fetching payments:", error);
      alert("Error fetching recurring payments");
    } finally {
      setLoading(false);
    }
  };

  // Simple status determination
  const getPaymentStatus = (payment) => {
    if (payment.status === "completed") {
      return { status: "completed", color: "default", icon: "✓" };
    }
    if (payment.status === "paused") {
      return { status: "paused", color: "warning", icon: "⏸" };
    }

    // Active payments - check if next payment is soon
    const today = new Date();
    if (payment.next_due_date) {
      const nextDue = new Date(payment.next_due_date);
      const daysUntilDue = Math.ceil((nextDue - today) / (1000 * 60 * 60 * 24));

      if (daysUntilDue <= 0) {
        return { status: "due today", color: "error", icon: "🔔" };
      }
      if (daysUntilDue <= 3) {
        return { status: "due soon", color: "warning", icon: "⏰" };
      }
    }

    return { status: "active", color: "success", icon: "✓" };
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm("Are you sure you want to delete this recurring payment?")
    )
      return;

    try {
      await deleteFunction(id);
      await fetchPayments();
    } catch (error) {
      console.error(error);
      alert("Failed to delete recurring payment.");
    }
  };

  const handlePause = async (id) => {
    try {
      await pauseFunction(id);
      await fetchPayments();
    } catch (error) {
      console.error(error);
      alert("Failed to pause payment.");
    }
  };

  const handleResume = async (id) => {
    try {
      await resumeFunction(id);
      await fetchPayments();
    } catch (error) {
      console.error(error);
      alert("Failed to resume payment.");
    }
  };

  const handleSort = (columnId) => {
    const isAsc = orderBy === columnId && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnId);
  };

  const handleFilterChange = (id, value) => {
    setSelectedFilters((prev) => ({ ...prev, [id]: value }));
  };

  // Build filter options
  const filterOptions = {
    category_type: new Set(),
    frequency: new Set(),
    status: new Set(),
  };

  payments.forEach((payment) => {
    filterOptions.category_type.add(payment.category_type);
    filterOptions.frequency.add(payment.frequency);
    const { status } = getPaymentStatus(payment);
    filterOptions.status.add(status);
  });

  // Filter payments
  const filteredPayments = payments.filter((payment) => {
    // Active filter
    if (showActiveOnly && payment.status !== "active") return false;

    // Dropdown filters
    const matchesFilters = Object.keys(selectedFilters).every((key) => {
      if (selectedFilters[key] === "ALL" || !selectedFilters[key]) return true;

      if (key === "status") {
        const { status } = getPaymentStatus(payment);
        return status === selectedFilters[key];
      }

      return payment[key] === selectedFilters[key];
    });

    // Search filter
    const matchesSearch =
      !searchQuery ||
      (payment.vendor &&
        payment.vendor.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (payment.note &&
        payment.note.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (payment.category_name &&
        payment.category_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase()));

    return matchesFilters && matchesSearch;
  });

  // Sort payments
  const sortedPayments = [...filteredPayments].sort((a, b) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    // Handle date sorting
    if (["start_date", "next_due_date", "end_date"].includes(orderBy)) {
      const dateA = aValue ? new Date(aValue) : new Date(0);
      const dateB = bValue ? new Date(bValue) : new Date(0);
      return order === "asc" ? dateA - dateB : dateB - dateA;
    }

    // Handle null values
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    // Handle numeric sorting
    const aNum = parseFloat(aValue);
    const bNum = parseFloat(bValue);
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return order === "asc" ? aNum - bNum : bNum - aNum;
    }

    // String sorting
    return order === "asc"
      ? aValue.toString().localeCompare(bValue.toString())
      : bValue.toString().localeCompare(aValue.toString());
  });

  // const formatCurrency = (amount) => {
  //   return `${getCurrencySymbol(userCurrency)}${parseFloat(amount).toFixed(2)}`;
  // };

  const formatRemainingPayments = (payment) => {
    if (payment.recurringcount === null || payment.recurringcount === undefined)
      return "∞";
    return payment.recurringcount > 0 ? payment.recurringcount : "0";
  };

  const formatNextPayment = (payment) => {
    if (!payment.next_due_date) return "—";

    const today = new Date();
    const nextDue = new Date(payment.next_due_date);
    const daysUntilDue = Math.ceil((nextDue - today) / (1000 * 60 * 60 * 24));

    if (daysUntilDue === 0) return `${payment.next_due_date} (Today)`;
    if (daysUntilDue === 1) return `${payment.next_due_date} (Tomorrow)`;
    if (daysUntilDue > 0 && daysUntilDue <= 7)
      return `${payment.next_due_date} (${daysUntilDue} days)`;

    return payment.next_due_date;
  };

  return (
    <TableContainer
      component={Paper}
      sx={{ maxWidth: "98%", margin: "auto", mt: 3 }}
    >
      <Typography variant="h5" sx={{ textAlign: "center", mb: 2, p: 2 }}>
        {title}
      </Typography>

      {/* Controls */}
      <Box
        sx={{
          mb: 2,
          ml: 1,
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
        }}
      >
        <Button variant="contained" onClick={() => navigate(addPath)}>
          Add Recurring Payment
        </Button>

        <FormControlLabel
          control={
            <Checkbox
              checked={showActiveOnly}
              onChange={(e) => setShowActiveOnly(e.target.checked)}
              color="primary"
            />
          }
          label="Show only active payments"
        />

        <TextField
          label="Search payments"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: "250px" }}
        />

        {/* Filters */}
        <Select
          value={selectedFilters.category_type || "ALL"}
          onChange={(e) => handleFilterChange("category_type", e.target.value)}
          size="small"
          sx={{ width: "150px" }}
        >
          <MenuItem value="ALL">All Types</MenuItem>
          {[...filterOptions.category_type].map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>

        <Select
          value={selectedFilters.frequency || "ALL"}
          onChange={(e) => handleFilterChange("frequency", e.target.value)}
          size="small"
          sx={{ width: "150px" }}
        >
          <MenuItem value="ALL">All Frequencies</MenuItem>
          {[...filterOptions.frequency].map((freq) => (
            <MenuItem key={freq} value={freq}>
              {freq}
            </MenuItem>
          ))}
        </Select>

        <Typography variant="body2" sx={{ ml: "auto" }}>
          {sortedPayments.length} of {payments.length} payments
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography>Loading...</Typography>
        </Box>
      ) : sortedPayments.length === 0 ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            {payments.length === 0
              ? "No recurring payments found."
              : "No payments match your current filters."}
          </Typography>
        </Box>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "vendor"}
                  direction={order}
                  onClick={() => handleSort("vendor")}
                >
                  Vendor
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "amount"}
                  direction={order}
                  onClick={() => handleSort("amount")}
                >
                  Amount
                </TableSortLabel>
              </TableCell>
              <TableCell>Category</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "frequency"}
                  direction={order}
                  onClick={() => handleSort("frequency")}
                >
                  Frequency
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "next_due_date"}
                  direction={order}
                  onClick={() => handleSort("next_due_date")}
                >
                  Next Payment
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "end_date"}
                  direction={order}
                  onClick={() => handleSort("end_date")}
                >
                  End Date
                </TableSortLabel>
              </TableCell>
              <TableCell>Remaining</TableCell>
              <TableCell>Note</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "user_name"}
                  direction={order}
                  onClick={() => handleSort("user_name")}
                >
                  User Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPayments.map((payment) => {
              const statusInfo = getPaymentStatus(payment);
              const isActive = payment.status === "active";

              return (
                <TableRow
                  key={payment.id}
                  sx={{
                    "&:hover": { backgroundColor: "action.hover" },
                    // Highlight payments due today/soon
                    ...(statusInfo.status === "due today" && {
                      backgroundColor: "rgba(211, 47, 47, 0.08)",
                    }),
                    ...(statusInfo.status === "due soon" && {
                      backgroundColor: "rgba(237, 108, 2, 0.08)",
                    }),
                  }}
                >
                  <TableCell>
                    <Chip
                      label={`${statusInfo.icon} ${statusInfo.status}`}
                      size="small"
                      color={statusInfo.color}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{payment.vendor || "—"}</TableCell>
                  <TableCell>
                    {formatCurrency(payment.amount, userCurrency)}
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Typography variant="body2">
                        {payment.category_name}
                      </Typography>
                      <Chip
                        label={payment.category_type}
                        size="small"
                        variant="outlined"
                        color={
                          payment.category_type === "INCOME"
                            ? "success"
                            : payment.category_type === "INVESTMENT"
                            ? "info"
                            : "default"
                        }
                      />
                    </Box>
                  </TableCell>
                  <TableCell>{payment.frequency}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatNextPayment(payment)}
                    </Typography>
                  </TableCell>
                  <TableCell>{payment.end_date || "Never"}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {formatRemainingPayments(payment)}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: "100px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <Typography variant="body2">
                      {payment.note || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {payment.user_name || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate(`${editPath}/${payment.id}`)}
                      >
                        Edit
                      </Button>

                      {isActive ? (
                        <Tooltip title="Pause automatic renewals">
                          <IconButton
                            size="small"
                            onClick={() => handlePause(payment.id)}
                            color="warning"
                          >
                            <Pause fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : payment.status === "paused" ? (
                        <Tooltip title="Resume automatic renewals">
                          <IconButton
                            size="small"
                            onClick={() => handleResume(payment.id)}
                            color="success"
                          >
                            <PlayArrow fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}

                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(payment.id)}
                      >
                        Delete
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );
};

export default RecurringPaymentsTable;
